import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useEffectiveAuth } from '../hooks/useEffectiveAuth'
import { listForms } from '../services/forms'
import { getMyTasks } from '../services/notifications'
import { getMyReviews, getMyReturned } from '../services/reviewService'
import { getMySubmissions } from '../services/submissions'
import { getSubmissionStats } from '../services/admin'
import './Dashboard.scss'

const Dashboard = () => {
  const { t } = useTranslation('common')
  const { logout } = useAuth()
  const { profile } = useEffectiveAuth()
  const navigate = useNavigate()

  // Check if user is a super admin (no tenant context)
  const isSuperAdmin = profile?.isSuperAdmin === true
  // Check if user is a tenant admin (has admin role but not super admin)
  const isTenantAdmin = profile?.role === 'admin' && !isSuperAdmin

  // Fetch dashboard stats (tasks, submissions) for regular users
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['my-tasks-summary'],
    queryFn: async () => {
      const [tasks, reviews, returned] = await Promise.all([
        getMyTasks(),
        getMyReviews(),
        getMyReturned(),
      ])
      return {
        sectionCount: tasks.tasks?.length || 0,
        reviewCount: reviews.reviews?.length || 0,
        returnedCount: returned.returned?.length || 0,
        totalTasks: (tasks.tasks?.length || 0) + (reviews.reviews?.length || 0) + (returned.returned?.length || 0),
        // Get most recent task for preview
        recentTask: tasks.tasks?.[0] || reviews.reviews?.[0] || returned.returned?.[0],
      }
    },
    enabled: !isSuperAdmin, // Only for regular users
    staleTime: 30000, // Consider fresh for 30 seconds
  })

  const { data: submissionsData, isLoading: submissionsLoading } = useQuery({
    queryKey: ['my-submissions-summary'],
    queryFn: async () => {
      const result = await getMySubmissions({ limit: 3 })
      return {
        // Prefer the server-provided total (#419). `submissions.length` is
        // capped by the `limit` above, so it can never exceed 3. Fall back to
        // the page length for older API responses without `totalCount`.
        count: result.totalCount ?? result.submissions?.length ?? 0,
        hasMore: result.hasMore,
        recent: result.submissions?.slice(0, 2) || [],
      }
    },
    enabled: !isSuperAdmin, // Only for regular users
    staleTime: 30000,
  })

  const { data: formsData, isLoading: formsLoading } = useQuery({
    queryKey: ['forms-summary'],
    queryFn: async () => {
      const result = await listForms({ limit: 3 })
      return {
        count: result.items?.length || 0,
        hasMore: result.hasMore,
        recent: result.items?.slice(0, 2) || [],
      }
    },
    staleTime: 30000,
  })

  // Admin stats query (only for tenant admins)
  const { data: adminStats, isLoading: adminStatsLoading } = useQuery({
    queryKey: ['admin-submission-stats'],
    queryFn: () => getSubmissionStats(),
    enabled: isTenantAdmin,
    staleTime: 60000, // Consider fresh for 1 minute
  })

  // Generate welcome message
  const getWelcomeMessage = () => {
    if (profile?.firstName) {
      return t('dashboard.welcomeUser', { name: profile.firstName })
    }
    return t('dashboard.welcome')
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-header__title">{t('navigation.dashboard')}</h1>
          <p className="dashboard-header__subtitle">
            {getWelcomeMessage()}
          </p>
        </div>
        <div className="dashboard-header__actions">
          <button
            onClick={logout}
            className="dashboard-btn dashboard-btn--ghost"
          >
            {t('buttons.logout')}
          </button>
        </div>
      </div>

      {/* Super Admin Notice */}
      {isSuperAdmin && (
        <div className="dashboard-super-admin-notice">
          <span className="dashboard-super-admin-notice__icon">⚡</span>
          <span className="dashboard-super-admin-notice__text">
            {t('dashboard.superAdminNotice')}
          </span>
        </div>
      )}

      {/* Dashboard Stats Cards */}
      {!isSuperAdmin && (
        <div className="dashboard-stats">
          {/* My Tasks Card */}
          <div
            className="dashboard-stats__card dashboard-stats__card--tasks"
            onClick={() => navigate('/my-tasks')}
          >
            <div className="dashboard-stats__card-icon">📋</div>
            <div className="dashboard-stats__card-content">
              <div className="dashboard-stats__card-count">
                {tasksLoading ? '—' : (tasksData?.totalTasks ?? 0)}
              </div>
              <div className="dashboard-stats__card-label">{t('dashboard.myTasks')}</div>
              {tasksData && tasksData.totalTasks > 0 && (
                <div className="dashboard-stats__card-detail">
                  {tasksData.sectionCount > 0 && `${tasksData.sectionCount} section${tasksData.sectionCount !== 1 ? 's' : ''}`}
                  {tasksData.sectionCount > 0 && tasksData.reviewCount > 0 && ', '}
                  {tasksData.reviewCount > 0 && `${tasksData.reviewCount} review${tasksData.reviewCount !== 1 ? 's' : ''}`}
                  {(tasksData.sectionCount > 0 || tasksData.reviewCount > 0) && tasksData.returnedCount > 0 && ', '}
                  {tasksData.returnedCount > 0 && `${tasksData.returnedCount} returned`}
                </div>
              )}
              {tasksData?.totalTasks === 0 && (
                <div className="dashboard-stats__card-detail">{t('dashboard.noPendingTasks')}</div>
              )}
            </div>
            <div className="dashboard-stats__card-arrow">→</div>
          </div>

          {/* My Submissions Card */}
          <div
            className="dashboard-stats__card dashboard-stats__card--submissions"
            onClick={() => navigate('/my-submissions')}
          >
            <div className="dashboard-stats__card-icon">📝</div>
            <div className="dashboard-stats__card-content">
              <div className="dashboard-stats__card-count">
                {submissionsLoading ? '—' : (submissionsData?.count ?? 0)}
              </div>
              <div className="dashboard-stats__card-label">{t('dashboard.mySubmissions')}</div>
              {submissionsData?.recent && submissionsData.recent.length > 0 && (
                <div className="dashboard-stats__card-detail">
                  Recent: {submissionsData.recent[0].formName || 'Submission'}
                </div>
              )}
              {submissionsData?.count === 0 && (
                <div className="dashboard-stats__card-detail">{t('dashboard.noSubmissions')}</div>
              )}
            </div>
            <div className="dashboard-stats__card-arrow">→</div>
          </div>

          {/* Forms Card */}
          <div
            className="dashboard-stats__card dashboard-stats__card--forms"
            onClick={() => navigate('/forms')}
          >
            <div className="dashboard-stats__card-icon">📄</div>
            <div className="dashboard-stats__card-content">
              <div className="dashboard-stats__card-count">
                {formsLoading ? '—' : (formsData?.count ?? 0)}
              </div>
              <div className="dashboard-stats__card-label">{t('navigation.forms')}</div>
              {formsData?.recent && formsData.recent.length > 0 && (
                <div className="dashboard-stats__card-detail">
                  {formsData.recent[0].name}
                </div>
              )}
              {formsData?.count === 0 && (
                <div className="dashboard-stats__card-detail">{t('dashboard.noForms')}</div>
              )}
            </div>
            <div className="dashboard-stats__card-arrow">→</div>
          </div>

          {/* Admin Stats Card - only for tenant admins */}
          {isTenantAdmin && (
            <div
              className="dashboard-stats__card dashboard-stats__card--admin"
              onClick={() => navigate('/admin/stats')}
            >
              <div className="dashboard-stats__card-icon">📊</div>
              <div className="dashboard-stats__card-content">
                <div className="dashboard-stats__card-count">
                  {adminStatsLoading ? '—' : (adminStats?.outstandingSections?.totalCount ?? 0)}
                </div>
                <div className="dashboard-stats__card-label">{t('dashboard.outstandingSections')}</div>
                <div className="dashboard-stats__card-detail">
                  {adminStatsLoading ? '—' : `${adminStats?.outstandingReviews?.totalCount ?? 0} pending reviews`}
                </div>
              </div>
              <div className="dashboard-stats__card-arrow">→</div>
            </div>
          )}
        </div>
      )}

      {/* Recent Activity Preview - Only for non-super admins */}
      {!isSuperAdmin && (formsData?.recent?.length > 0 || submissionsData?.recent?.length > 0) && (
        <div className="dashboard-recent">
          <h2 className="dashboard-recent__title">{t('dashboard.recentActivity')}</h2>
          <div className="dashboard-recent__grid">
            {formsData?.recent?.slice(0, 2).map((form) => (
              <div
                key={form.id}
                className="dashboard-recent__item"
                onClick={() => navigate(`/forms/${form.id}/edit`)}
              >
                <div className="dashboard-recent__item-icon">📄</div>
                <div className="dashboard-recent__item-content">
                  <div className="dashboard-recent__item-title">{form.name}</div>
                  <div className="dashboard-recent__item-meta">
                    Form • {form.submissionCount || 0} submissions
                  </div>
                </div>
                <div className="dashboard-recent__item-date">
                  {formatDate(form.updatedAt || form.createdAt)}
                </div>
              </div>
            ))}
            {submissionsData?.recent?.slice(0, 2).map((submission) => (
              <div
                key={submission.submissionId}
                className="dashboard-recent__item"
                onClick={() => navigate(`/submissions/${submission.formId}/${submission.submissionId}`)}
              >
                <div className="dashboard-recent__item-icon">📝</div>
                <div className="dashboard-recent__item-content">
                  <div className="dashboard-recent__item-title">{submission.formName || 'Submission'}</div>
                  <div className="dashboard-recent__item-meta">
                    Submission • {submission.status || 'Draft'}
                  </div>
                </div>
                <div className="dashboard-recent__item-date">
                  {formatDate(submission.updatedAt || submission.createdAt)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard
